import BouncingSpinner from 'components/BouncingSpinner';
import Download from 'components/Icons/Download';
import { Button } from 'components/lib';
import OutsideClick from 'components/OutsideClick';
import React, { useState, useContext } from 'react';
import { Manager, Popper, Reference } from 'react-popper';
import { ExportSortDirection } from 'records/Export';
import { Project } from 'records/Project';
import { exportXLSXRFis } from 'services/rfiService';
import { downloadFileWithAnchor, getDateString } from 'utils/general';
import * as toast from 'utils/toast';
import withAppStore from 'utils/withAppStore';
import AppStoreContext from 'contexts/AppStoreContext';
import { useTranslator } from '@bluebeam/react-i18n';

const RFIExportButton = () => {
  const [downloading, setDownloading] = useState<boolean>(false);
  const [open, setOpen] = useState<boolean>(false);
  const { state } = useContext(AppStoreContext);

  const { translate } = useTranslator(['buttons', 'toastText']);

  const handleDownloadClick = (): void => {
    setOpen(!open);
  };

  const handleOutsideClick = (): void => {
    setOpen(false);
  };

  const handleXLSXClick = async (): Promise<void> => {
    try {
      setDownloading(true);
      setOpen(false);

      const { projects, selectedProject, queriedRFIs, rfiTableQuery } = state;
      const { name } = projects.get(selectedProject, new Project());
      const sortdirection =
        rfiTableQuery.sortDirection === 'ascending'
          ? ExportSortDirection.ASCENDING
          : ExportSortDirection.DESCENDING;

      const { url, extension } = await exportXLSXRFis(
        selectedProject,
        queriedRFIs,
        rfiTableQuery.sortField,
        sortdirection
      );

      const filename = `${name} RFI Log ${getDateString()}.${extension}`;

      await downloadFileWithAnchor(url, filename);
    } catch (err) {
      toast.error(
        translate('toastText:rfiDownloadLogError.message'),
        translate('toastText:rfiDownloadLogError.title')
      );
      console.log('Unable to download RFI Log', err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Manager>
      <Reference>
        {({ ref }): React.ReactNode => (
          <div ref={ref} className="hs2 flex items-center">
            {downloading ? (
              <div className="flex-center mt1 mr4">
                <BouncingSpinner />
              </div>
            ) : (
              <Button
                id="rfi-export-btn-export-btn"
                className="row items-center justify-between mr2"
                onClick={handleDownloadClick}
                variant="tertiary"
                size="small"
              >
                <div className="mr2">
                  <Download width="14px" height="12px" />
                </div>
                <div className="font-size-4">{translate('buttons:export')}</div>
              </Button>
            )}
          </div>
        )}
      </Reference>
      <Popper>
        {({ ref, style, placement }): React.ReactNode => {
          return open ? (
            <div
              ref={ref}
              className="bgd-white wrem-9 b-solid b-2"
              style={{
                ...style,
                zIndex: 2,
              }}
              data-placement={placement}
            >
              <OutsideClick onOutsideClick={handleOutsideClick}>
                <div className="column items-center color-primary-dark">
                  <div className="w100 flex-center">
                    <div
                      className="m2 py1 cursor-pointer"
                      onClick={handleXLSXClick}
                    >
                      {translate('buttons:exportAs')}.xlsx
                    </div>
                  </div>
                </div>
              </OutsideClick>
            </div>
          ) : null;
        }}
      </Popper>
    </Manager>
  );
};

export default withAppStore(RFIExportButton);
